import React, { ChangeEvent, forwardRef, ReactNode, RefAttributes } from 'react';
import { MixProps } from '@visalex/core';

import cx from 'clsx';

import { BaseFieldProps } from '../../Form/utils/connectFields/connectFields.utils';

import radioStyles from './Radio.module.scss';

export type RadioProps = Omit<
  MixProps<
    BaseFieldProps<string> & {
      /**
       * Validity of the field: valid=false = invalid, valid=null|undefined = not yet validated
       */
      valid?: boolean | null;

      /**
       * Whether this input is in indeterminable state
       */
      indeterminate?: boolean;

      /**
       * The special props that allows to extend this component without change of the css
       */
      type?: 'radio' | 'checkbox' | 'toggle';

      /**
       * Custom mark allows to extend the radio box
       */
      customMark?: ReactNode;

      styles?: { [key: string]: string };
      onChange?: (newValue: string, e: ChangeEvent<HTMLInputElement>) => void;
    } & RefAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  'label'
>;

/**
 * Component that represents a radio with a label
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      customMark,
      className,
      checked,
      indeterminate = false,
      disabled = false,
      children,
      styles = radioStyles,
      valid,
      type = 'radio',
      onChange,
      defaultValue: _,
      ...rest
    },
    ref
  ) => {
    return (
      <label
        className={cx([
          className,
          styles[type],
          !indeterminate && checked && styles[`${type}--checked`],
          disabled && styles[`${type}--disabled`],
          typeof valid === 'boolean' && styles[`${type}--${valid ? 'valid' : 'invalid'}`],
          indeterminate && styles[`${type}--indeterminate`],
        ])}
      >
        <input
          {...rest}
          ref={ref}
          type={type === 'radio' ? 'radio' : 'checkbox'}
          checked={checked}
          onChange={(e) => onChange && onChange(e.target.value, e)}
          disabled={disabled}
          className={styles[`${type}__input`]}
          data-testid={`${type}-component`}
        />
        <i className={styles[`${type}__mark`]} data-testid="mark-icon">
          {customMark && customMark}
        </i>
        <span className={styles[`${type}__label`]}>{children}</span>
      </label>
    );
  }
);

Radio.displayName = 'Radio';
